<template>
    <div>
        <AocDuplicate v-if="isDuplicateAreaPage" />
        <AocStaging v-if="isHomePage" />
        <AocSearch v-if="isSearchPage" />
        <AocQuestions v-if="isQuestionsPage" />
        <AocEquipment v-if="isEquipmentPage" />
        <!-- ONLY DISPLAY AFTER FIRST AOC IS ADDED -->
        <AocAddedPrompt v-if="sectionIndex === 3" />

        <section class="assessmentSection questionsSection no-padding"
                 v-if="isHomePage">
            <div class="pageContent">
                <continue-button to="/assessment/loading"
                                 v-if="isHomePage"
                                 :before="mixpanelTracking"
                                 text="Continue to my system" />
            </div>
        </section>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import uuid from 'uuid/v1'
import AocStaging from './AocStaging.vue'
import AocSearch from './areas-of-concern/AocSearch.vue'
import AocQuestions from './areas-of-concern/AocQuestions.vue'
import AocEquipment from './areas-of-concern/AocEquipment.vue'
import AocConfirmation from './areas-of-concern/AocConfirmation.vue'
import AocAddedPrompt from './areas-of-concern/AocAddedPrompt.vue'
import AocDuplicate from './areas-of-concern/AocDuplicate.vue'
import AocView from '../components/AocView.vue'

export default {
    data() {
        return {
            emptyAreaOfConcern: {},
            sections: [
                'AocQuestions',
                'AocEquipment',
                'AocConfirmation',
                'AocAddedPrompt'
            ]
        }
    },
    computed: {
        ...mapState([
            'lead',
            'areaOfConcern',
            'availableAreas',
            'areaOfConcernSectionIndex'
        ]),

        sectionIndex() {
            return this.areaOfConcernSectionIndex
        },

        areaSelected() {
            return this.areaOfConcern.name !== ''
        },

        isTotalProtectionLevel() {
            return this.lead.protectionLevel === 'Total'
        },

        basementProtected() {
            let basement = this.lead.areasOfConcern.filter(
                area => area.name === 'Basement'
            )
            return basement.length > 0
        },

        adviseToProtectBasement() {
            if (
                this.isTotalProtectionLevel &&
                !this.basementProtected &&
                !this.areaSelected &&
                !this.isQuestionsPage &&
                !this.isEquipmentPage &&
                !this.isConfirmationPage
            ) {
                return true
            }

            return false
        },

        hasEquipment() {
            let keys = Object.keys(this.areaOfConcern.equipmentOptions)
            let length = keys.length
            return length > 0
        },

        hasAreasOfConcern() {
            return this.lead.areasOfConcern.length > 0
        },

        isDuplicateAreaPage() {
            return this.sectionIndex === -2
        },

        isHomePage() {
            return this.sectionIndex === -1
        },

        isSearchPage() {
            return this.sectionIndex === 0
        },

        isQuestionsPage() {
            return this.sectionIndex === 1
        },

        isEquipmentPage() {
            return this.sectionIndex === 2
        },

        isConfirmationPage() {
            return this.sectionIndex === 3
        },

        toEquipment() {
            let aoc = this.areaOfConcern

            if (
                !this.isChildView &&
                aoc.accessibleFromGroundFloor &&
                aoc.windowsThatOpen
            ) {
                return true
            }

            if (
                !this.isChildView &&
                !aoc.accessibleFromGroundFloor &&
                aoc.accessOverride &&
                aoc.windowsThatOpen
            ) {
                return true
            }

            return false
        },

        toConfirmation() {
            let aoc = this.areaOfConcern
            if (
                !this.isChildView &&
                aoc.accessibleFromGroundFloor == false &&
                !aoc.accessOverride
            ) {
                return true
            }
            if (this.isEquipmentPage && aoc.equipmentOptionChosen) {
                return true
            }
            return false
        },

        inputDirty() {
            return this.areaOfConcern.name ? true : false
        },

        isChildView() {
            let route = this.$route.path
            let views = ['equipment', 'confirmation']

            for (var index = 0; index < views.length; index++) {
                let routeIsChild = route.indexOf(views[index]) > -1
                if (routeIsChild) {
                    return true
                }
            }

            return false
        }
    },
    methods: {
        ...mapActions([
            'addAreaOfConcern',
            'closeAreasOfConcern',
            'updateAreaOfConcern',
            'save',
            'incrementAocSectionIndex',
            'decrementAocSectionIndex',
            'setAocSectionIndex'
        ]),

        mixpanelTracking() {
            let data = {
                P_AOC_numberOfRooms: this.lead.areasOfConcern.length
            }
            mixpanel.track('completedAreaOfConcern', data)
            mixpanel.people.set(data)
        },

        toggleInterestedInMotion() {
            this.updateAreaOfConcern({
                interestedInMotion: !this.areaOfConcern.interestedInMotion
            })
            window.scrollTo(0, 200)
        },

        protectBasement() {
            let basement = this.availableAreas.filter(
                area => area.name === 'Basement'
            )[0]
            basement.id = uuid()
            this.updateAreaOfConcern(basement)
        },

        next() {
            window.scrollTo(0, 0)
            // Has areas added and confirmation
            if (this.isEquipmentPage) {
                this.addArea()
                if (this.lead.areasOfConcern.length > 1) {
                    this.resetArea()
                    return
                }
            }
            this.incrementAocSectionIndex()
        },

        resetArea() {
            // Empty the local area of concern
            this.updateAreaOfConcern(this.emptyAreaOfConcern)
            this.setAocSectionIndex(-1)
        },

        addArea() {
            this.addAreaOfConcern(this.areaOfConcern)
            this.save()
        },

        selectEquipment(type) {
            this.updateAreaOfConcern({
                equipmentOptionChosen: type
            })
        },

        analyzeArea() {
            // Determine window equipment
            let params = {
                area: this.areaOfConcern
            }
            axios
                .get('/ajax/true-advice/area-of-concern', {
                    params
                })
                .then(res => {
                    let data = res.data
                    this.updateAreaOfConcern(data)
                    this.$router.push('/assessment/areas-of-concern/equipment')
                })
        },

        selectRoom(areaOfConcern) {
            this.updateAreaOfConcern(areaOfConcern)
        }
    },
    mounted() {
        this.$Progress.set(77)
        this.emptyAreaOfConcern = {
            ...this.areaOfConcern
        }
        // this.resetArea()
    },

    updated() {
        // Redirect back if no area of concern selected
        if (!this.areaOfConcern.name && this.isChildView) {
            this.$router.replace('/assessment/areas-of-concern')
        }
    },
    components: {
        AocStaging,
        AocSearch,
        AocQuestions,
        AocEquipment,
        AocConfirmation,
        AocAddedPrompt,
        AocView,
        AocDuplicate
    }
}
</script>

<style lang="css">

</style>
